import React, { useState } from "react";
import "./FlashCards.css";
import { Seo } from "../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../Landing/IntroParagraphs.tsx";
import { findBySlug } from "../Shared/Seo/routeSeo.ts";
import { Operations } from "../MathFactsGenerator/Constants.ts";
import { labelFor } from "../MathFactsGenerator/Labels.ts";
import { SettingsProvider } from "../MathFactsGenerator/Settings/SettingsStore.tsx";
import { Operators } from "../MathFactsGenerator/Settings/Operators.tsx";
import { ValueRange } from "../MathFactsGenerator/Settings/ValueRange.tsx";
import { flashCardSettings } from "./deck.ts";
import { FlashCardDeck } from "./FlashCardDeck.tsx";
import { PrintPanel } from "./PrintPanel.tsx";
import { MissedFactsPractice } from "./Quiz/MissedFactsPractice.tsx";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";

interface FlashCardsPageProps {
    slug: string;
    operation: Operations;
}

/**
 * One flash-card route: a keyword landing page (h1 / intro / FAQ from the shared
 * manifest, like every other page) wrapped around the interactive deck.
 *
 * Settings are held here as ISettingsProps and provided through the same
 * SettingsContext the worksheet generator uses, so the existing Operators and
 * ValueRange controls drive the deck unmodified. There is no PDF anywhere in
 * this feature, so no lazy boundary and nothing added to the initial bundle
 * beyond these components.
 */
export function FlashCardsPage({ slug, operation }: FlashCardsPageProps) {
    const entry = findBySlug(slug)!;
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    const t = useTranslation();

    return (
        <SettingsProvider settings={settings} setSettings={setSettings}>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="flashCardsTool">
                <FlashCardDeck />

                {/* Practice sheet built from facts missed on the quiz pages.
                    Renders nothing until there is something to practise. */}
                <MissedFactsPractice />

                <details className="flashCardSettings">
                    <summary>{t('flashCards.changeWhatToPractice')}</summary>
                    <Operators />
                    <div className="valueRangeContainer">
                        <ValueRange RangeSettings={0} />
                        <ValueRange RangeSettings={1} />
                    </div>
                </details>

                {/* The same settings, printed. Everything below here is behind a
                    lazy import, so the PDF renderer only loads if a visitor
                    scrolls down and actually wants paper. */}
                <PrintPanel operationLabel={labelFor(operation).toLowerCase()} />
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t('flashCards.frequentlyAskedQuestions')}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </SettingsProvider>
    );
}

export default FlashCardsPage;
